import React, { useEffect, useState } from "react";
import {
  Descriptions,
  Typography,
  Result,
  Tag,
  Card,
  Space,
  Divider,
  notification,
} from "antd";
import {
  TrophyOutlined,
  SmileOutlined,
  FrownOutlined,
} from "@ant-design/icons";
import { useAppDispatch } from "../../../store/hooks";
import { setcurrentSelectedMenu } from "../../../store/ui/reducer";
import { Link } from "react-router-dom";
import ScratchReveal from "./ScratchReveal";
import { userActions } from "../../../store/user";
import HAVETOApi from "../../../services/api/scratchcard/api";

interface CardDetailProps {
  card: any;
  closeModal?: () => void;
}

const CardDetail: React.FC<CardDetailProps> = ({
  card: initialCard,
  closeModal,
}) => {
  const dispatch = useAppDispatch();
  const revealScratchCardAPI = HAVETOApi.revealScratchCard;
  const [card, setCard] = useState(initialCard);
  const [resetKey, setResetKey] = useState(0);
  useEffect(() => {
    setCard(initialCard);
    setResetKey((prev) => prev + 1);
  }, [card?.id, initialCard]);

  const handleReveal = async (card_id: number) => {
    try {
      const result = await revealScratchCardAPI({ card_id: String(card_id) });
      if (result.error) {
        notification.error({
          message: result.error.message || "Error while revealing the card!",
        });
      } else {
        notification.success({
          message: "Card Scratched successfully!",
        });
        setCard(result.scratchcard.data);
      }

      dispatch(userActions.getScratchCards());
    } catch (error) {
      notification.error({
        message: "Failed to reveal the scratch card. Please try again.",
      });
    }
  };
  if (!card) return null;
  const isScratchedOrClaimed = !!(card.scratched_at || card.claimed_at);

  // Status-based color / gradient
  const statusColors: Record<string, string> = {
    claimed: "linear-gradient(135deg, #fffbe6 0%, #faffd1 100%)",
    scratched: "linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)",
    pending: "linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)",
    expired: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)",
  };

  const cardBg =
    statusColors[card.status?.toLowerCase()] ||
    "linear-gradient(135deg, #f9f9f9 0%, #eaeaea 100%)";

  return (
    <Card
      style={{
        borderRadius: 16,
        boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
        overflow: "hidden",
      }}
      bodyStyle={{ padding: 0 }}
    >
      {/* Gradient Header */}
      <div
        style={{
          background: cardBg,
          padding: "16px 24px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Typography.Title
          level={4}
          style={{
            margin: 0,
            color: "#222",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {card.is_winner ? (
            <TrophyOutlined style={{ color: "#fadb14" }} />
          ) : (
            <SmileOutlined />
          )}
          Scratch Card #{card.id}
        </Typography.Title>

        <Space style={{ marginTop: 8 }}>
          <Tag color={card.status === "claimed" ? "green" : "blue"}>
            {card.status?.toUpperCase()}
          </Tag>
          {card.is_winner && <Tag color="gold">WINNER 🎉</Tag>}
          <Tag
            color="purple"
            onClick={() => {
              dispatch(setcurrentSelectedMenu("/transaction"));
            }}
          >
            <Link to="/transaction">Veiw Transactions</Link>
          </Tag>
        </Space>
      </div>

      {/* Body */}
      <div>
        {isScratchedOrClaimed ? (
          <>
            {card.is_winner ? (
              <Result
                style={{ padding: "5px" }}
                icon={<TrophyOutlined style={{ color: "#fadb14" }} />}
                title="Congratulations!"
                subTitle="You’ve won this card — Scratched successfully 🎉"
              />
            ) : (
              <Result
                icon={<FrownOutlined />}
                title="Better luck next time!"
                subTitle="But keep scratching!"
              />
            )}
            <Divider />
            <Descriptions bordered size="middle" column={1}>
              <Descriptions.Item label="Win Amount">
                <Typography.Text
                  strong
                  style={{ fontSize: 18, color: "#52c41a" }}
                >
                  {Number(card.win_amount || 0).toFixed(2)} <sub>HVT</sub>
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {new Date(card.created_at).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Scratched At">
                {card.scratched_at
                  ? new Date(card.scratched_at).toLocaleString()
                  : "Not yet"}
              </Descriptions.Item>
              <Descriptions.Item label="Claimed At">
                {card.claimed_at
                  ? new Date(card.claimed_at).toLocaleString()
                  : "Not yet"}
              </Descriptions.Item>
            </Descriptions>
          </>
        ) : (
          <ScratchReveal
            key={`${card.id}-${resetKey}`}
            cardId={card.id}
            rewardText={`Card Scratched!`}
            amount={card.win_amount}
            scratched={!!card.scratched_at}
            onScratchComplete={() => {
              handleReveal(card.id);
              closeModal && closeModal();
            }}
          />
        )}
      </div>
    </Card>
  );
};

export default CardDetail;
